import React, {useCallback, useState, useEffect} from 'react';
import {FormControl} from '@mui/material';
import {LocationInput} from '@/components/forms/input/location';
import {useTranslation} from 'react-i18next';
import {
  Location as LocationType,
  LocationExtended
} from '@/schemas/sub-schemas';
import {FilterLocation, FilterDocumentType} from '@/types/filter';

interface LocationFilterProps {
  filterType: FilterDocumentType;
  filterLocation: FilterLocation | undefined;
  filterLocationLabel: string | undefined;
  onLocationChange: (
    newLocation: LocationType | LocationExtended | null
  ) => void;
}

const LocationFilter: React.FC<LocationFilterProps> = ({
  filterType,
  filterLocation,
  filterLocationLabel,
  onLocationChange
}) => {
  const {t} = useTranslation();

  const [locations, setLocations] = useState<{
    [key in FilterDocumentType]?: LocationType | null;
  }>({});

  useEffect(() => {
    if (filterLocation) {
      setLocations((prev) => ({
        ...prev,
        [filterType]: {
          lat: filterLocation.coordinates[1],
          lng: filterLocation.coordinates[0],
          label: filterLocationLabel || ''
        }
      }));
    } else {
      setLocations((prev) => ({
        ...prev,
        [filterType]: null
      }));
    }
  }, [filterLocation, filterType, filterLocationLabel]);

  const handleLocationChange = useCallback(
    (newLocation: LocationType | LocationExtended | null) => {
      const locationToSave = newLocation
        ? {
            lat: newLocation.lat,
            lng: newLocation.lng,
            label: newLocation.label
          }
        : null;

      setLocations((prev) => ({
        ...prev,
        [filterType]: locationToSave
      }));

      onLocationChange(newLocation);
    },
    [onLocationChange, filterType]
  );

  return (
    <FormControl fullWidth>
      <LocationInput
        name="location"
        label={t('location')}
        value={locations[filterType] || null}
        initialLocation={locations[filterType] || null}
        onChange={handleLocationChange}
        locationExtended={true}
        showCircle={true}
        allowClear={true}
        height={'500px'}
        filterType={filterType}
      />
    </FormControl>
  );
};

export default LocationFilter;
